import Card from "@/components/Card"
import FormHeader from "@/components/FormHeader"
import InputError from "@/components/InputError"
import Modal from "@/components/Modal"
import SkeletonRow from "@/components/SkeletonRow"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { BackgroundGradient } from "@/components/ui/background-gradient"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { web } from "@/config/System"
import useApiBaseUrl from "@/hooks/useApiBaseUrl"
import usePaymentMethod from "@/hooks/usePaymentMethod"
import useSessionStorage from "@/hooks/useSessionStorage"
import { useUser } from "@/hooks/useUser"
import api from "@/lib/axios"
import { formatDateIndonesia, formatRupiah } from "@/lib/utils"
import { getBiodata } from "@/services/getData"
import {
  DataPendaftaran,
  DataSiswa,
  DataTagihanDaftarPlaza,
  PaymentMethod,
} from "@/types"
import { AxiosError } from "axios"
import { ChevronRight, Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaBitcoin, FaCheck } from "react-icons/fa6"
import { MdOutlineContentCopy } from "react-icons/md"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"

const getInformasiTagihan = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/informasi-tagihan?id_sys_periode=${id_sys_periode}`
  )
  return response.data?.data
}

const generateTagihanPendaftaran = async (
  id_sys_periode: string,
  id_data_pendaftaran: string,
  payment_gateway_id: number | null
) => {
  const response = await api.post(
    `/siswa/penerimaan/smbjm/validasi/generate-tagihan-daftar`,
    { id_sys_periode, id_data_pendaftaran, payment_gateway_id }
  )
  return response.data
}

const getInvoice = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/invoice-tagihan?id_data_pendaftaran=${id_data_pendaftaran}`
  )
  return response.data?.data
}

const getStatusPembayaran = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/validasi/cek-status-pembayaran-daftar?id_sys_periode=${id_sys_periode}`
  )
  return response.data
}

export default function KartuPeserta() {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  })
  const { user } = useUser()
  const { apiBaseUrl } = useApiBaseUrl()

  const [showGenerateTagihan, setShowGenerateTagihan] = useState(false)
  const [showTagihan, setShowTagihan] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null)
  const [selectedTagihan, setSelectedTagihan] =
    useState<DataTagihanDaftarPlaza | null>(null)
  const [isCopied, setIsCopied] = useState(false)

  const { listPaymentMethodPlaza, isPendingListPaymentMethodPlaza } =
    usePaymentMethod()

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
    })
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined

  const {
    data: dataInformasiTagihan,
    isPending: isPendingDataInformasiTagihan,
  } = useQuery({
    queryKey: ["informasiTagihan", sessionPeriode?.id_sys_periode],
    queryFn: () => getInformasiTagihan(sessionPeriode?.id_sys_periode),
    enabled: !!sessionPeriode?.id_sys_periode,
  })

  const {
    data: listDataInvoice,
    isPending: isPendingListDataInvoice,
    isFetched: isFetchedDataInvoice,
  } = useQuery({
    queryKey: ["listDataInvoice", dataPendaftaran?.id_data_pendaftaran],
    queryFn: () =>
      getInvoice(String(dataPendaftaran?.id_data_pendaftaran) || ""),
    enabled: !!dataPendaftaran?.id_data_pendaftaran,
  })

  const {
    data: dataStatusPembayaran,
    isPending: isPendingDataStatusPembayaran,
    isFetched: isFetchedDataStatusPembayaran,
  } = useQuery({
    queryKey: ["dataStatusPembayaran", sessionPeriode?.id_sys_periode],
    queryFn: () => getStatusPembayaran(sessionPeriode?.id_sys_periode),
    enabled: !!sessionPeriode?.id_sys_periode && listDataInvoice?.length > 0,
  })

  // const {
  //   data: dataInformasiRuangan,
  //   isPending: isPendingDataInformasiRuangan,
  //   isFetched: isFetchedDataInformasiRuangan,
  // } = useQuery({
  //   queryKey: ["dataInformasiRuangan", sessionPeriode?.id_sys_periode],
  //   queryFn: () => getInformasiRuangan(sessionPeriode?.id_sys_periode),
  //   enabled: !!sessionPeriode?.id_sys_periode,
  // });

  const queryClient = useQueryClient()
  const {
    mutate: mutateGenerateTagihan,
    isPending: isPendingGenerateTagihan,
    error: errorGenerateTagihan,
  } = useMutation({
    mutationFn: () =>
      generateTagihanPendaftaran(
        sessionPeriode?.id_sys_periode || "",
        String(dataPendaftaran?.id_data_pendaftaran) || "",
        selectedPaymentMethod?.id ?? null
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataInvoice"] })
      toast.success(data?.message)
      setShowGenerateTagihan(false)
    },
    onError: (
      error: AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleGenerateTagihanPendaftaran = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutateGenerateTagihan()
  }

  const isSudahGenerateTagihan =
    isFetchedDataInvoice && listDataInvoice?.length > 0
  const isSudahBayar =
    isFetchedDataStatusPembayaran &&
    dataStatusPembayaran?.data &&
    dataStatusPembayaran?.data?.status_pembayaran === "SUDAH_BAYAR"

  // const isSudahDownloadKartuPeserta = JSON.parse(
  //   localStorage.getItem("is_download_kartu_peserta_rpl") ?? "false"
  // );

  const isSudahDownloadKartuPeserta = false

  return (
    <div key="kartupeserta-smbjm-prestasi">
      <h1 className="mb-5 font-bold text-xl">Kartu Peserta</h1>
      <Alert variant={"warning"} className="mb-3">
        <AlertTitle>Perhatian!!!</AlertTitle>
        <AlertDescription>
          Pastikan Anda telah melakukan pembayaran biaya pendaftaran dan
          mencetak kartu peserta.{" "}
          <span className="font-bold">
            Hanya peserta yang telah mendapatkan kartu peserta yang dapat
            diikutkan dalam seleksi.
          </span>
        </AlertDescription>
      </Alert>
      <section className="flex flex-col gap-y-3">
        {isPendingDataSiswa ||
        isPendingDataInformasiTagihan ||
        isPendingListDataInvoice ? (
          <SkeletonRow />
        ) : (
          <Card>
            <div className="flex items-center gap-5">
              <div
                className={`w-10 h-10 border rounded-full flex justify-center items-center ${
                  isSudahGenerateTagihan ? "bg-success text-white" : ""
                }`}
              >
                {isSudahGenerateTagihan ? <FaCheck /> : "1"}
              </div>
              <div className="flex-1 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-bold">Generate Tagihan</span>

                  {isSudahBayar ? (
                    <Badge variant={"success"}>Sudah dibayar</Badge>
                  ) : isSudahGenerateTagihan ? (
                    <Badge variant={"destructive"}>Belum dibayar</Badge>
                  ) : (
                    <Badge variant={"secondary"}>Belum dibuat</Badge>
                  )}
                </div>
                {!isSudahGenerateTagihan && !isSudahBayar ? (
                  <Button
                    className="sm:w-[14rem] w-full"
                    onClick={() => setShowGenerateTagihan(true)}
                  >
                    Buat Tagihan
                  </Button>
                ) : (
                  !isSudahBayar && (
                    <Button
                      className="sm:w-[14rem] w-full"
                      onClick={() => setShowGenerateTagihan(true)}
                    >
                      Buat Tagihan Ulang
                    </Button>
                  )
                )}
              </div>
            </div>
            <div className="ml-16">
              <p>Nama: {dataSiswa?.nama_lengkap}</p>
              <p>
                Biaya Pendaftaran:{" "}
                {formatRupiah(Number(dataInformasiTagihan?.biaya_daftar ?? 0))}
              </p>
              {/* <p>
                Biaya Portofolio:{" "}
                {formatRupiah(
                  Number(dataInformasiTagihan?.biaya_portofolio ?? 0)
                )}
                <Badge variant={"success"}>Gratis</Badge>
              </p> */}
              <div className="my-1 border-b-2 border-dashed" />
              <p>
                Total Biaya:{" "}
                <span className="font-bold">
                  {formatRupiah(
                    Number(dataInformasiTagihan?.total_tagihan ?? 0)
                  )}
                </span>
              </p>
            </div>
          </Card>
        )}
        {isPendingListDataInvoice || isPendingDataStatusPembayaran ? (
          <SkeletonRow />
        ) : (
          <Card className="flex items-center gap-5">
            <div
              className={`w-10 h-10 border rounded-full flex justify-center items-center ${
                isSudahBayar ? "bg-success text-white" : ""
              }`}
            >
              {isSudahBayar ? <FaCheck /> : "2"}
            </div>
            <div className="flex-1 flex flex-wrap items-center justify-between gap-3">
              <span className="font-bold">
                {isSudahBayar
                  ? "Sudah Melakukan Pembayaran"
                  : "Lakukan Pembayaran"}
              </span>
              {isSudahGenerateTagihan && (
                <div className="flex flex-col gap-3">
                  {!isSudahBayar && (
                    <Button
                      className="sm:w-[14rem] w-full"
                      onClick={() =>
                        window.open(
                          `${apiBaseUrl}/siswa/penerimaan/smbjm/pendaftaran/slip-pembayaran?id_sys_periode=${sessionPeriode?.id_sys_periode}`
                        )
                      }
                    >
                      Unduh Slip Pembayaran
                    </Button>
                  )}
                  <Button
                    variant={"dark"}
                    className="sm:w-[14rem] w-full"
                    onClick={() => setShowTagihan(true)}
                  >
                    {isSudahBayar ? "Lihat Pembayaran" : "Lihat Tagihan"}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}
        {/* {isPendingDataInformasiRuangan ? (
          <SkeletonRow />
        ) : (
          <Card className="flex items-center gap-5">
            <div
              className={`w-10 h-10 border rounded-full flex justify-center items-center ${
                isSudahDistribusiRuangan ? "bg-success text-white" : ""
              }`}
            >
              {isSudahDistribusiRuangan ? <FaCheck /> : "3"}
            </div>
            <div className="flex-1 flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="font-bold">
                  Distribusi Lokasi & Ruang Ujian
                </span>
                {dataStatusPembayaran &&
                  dataStatusPembayaran["status"] &&
                  dataStatusPembayaran["status"] == false &&
                  dataStatusPembayaran?.message && (
                    <Alert variant={"warning"} className="mt-3">
                      <AlertTitle></AlertTitle>
                      <AlertDescription>
                        <div>{dataStatusPembayaran?.message}</div>
                      </AlertDescription>
                    </Alert>
                  )}
              </div>
              {isSudahBayar && (
                <Button
                  className="sm:w-[14rem] w-full"
                  onClick={() => setShowInformasiRuangan(true)}
                >
                  Lihat Lokasi & Ruang Ujian
                </Button>
              )}
            </div>
          </Card>
        )} */}
        <Card className="flex items-center gap-5">
          <div
            className={`w-10 h-10 border rounded-full flex justify-center items-center ${
              isSudahDownloadKartuPeserta ? "bg-success text-white" : ""
            }`}
          >
            {isSudahDownloadKartuPeserta ? <FaCheck /> : "3"}
          </div>
          <div className="flex-1 flex flex-wrap items-center justify-between gap-3">
            <span className="font-bold">Cetak Kartu Peserta</span>
            {isSudahBayar && (
              <Button
                variant={"dark"}
                className="sm:w-[14rem] w-full"
                onClick={() => {
                  localStorage.setItem("is_download_kartu_peserta_rpl", "true")
                  window.open(
                    `${apiBaseUrl}/siswa/penerimaan/smbjm/kartu-peserta/rpl?id_sys_periode=${sessionPeriode?.id_sys_periode}`
                  )
                }}
              >
                Download
              </Button>
            )}
          </div>
        </Card>
      </section>

      <Modal
        show={showGenerateTagihan}
        onClose={() => setShowGenerateTagihan(false)}
      >
        <FormHeader
          title="Generate Tagihan"
          icon={<FaBitcoin size={20} />}
          description={`
        Pilih metode pembayaran yang akan digunakan, kemudian tekan tombol "Buat Tagihan"`}
        />
        <form onSubmit={handleGenerateTagihanPendaftaran}>
          <h1 className="font-bold text-sm">TAGIHAN</h1>
          {isPendingDataSiswa || isPendingDataInformasiTagihan ? (
            <div className="flex flex-col gap-y-3">
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : (
            <>
              <div className="flex justify-between">
                <span className="font-semibold">Nomor Pendaftaran</span>
                <span className="font-semibold text-muted-foreground">
                  {dataPendaftaran?.nomor_pendaftaran}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="font-semibold">Nama</span>
                <span className="font-semibold text-end text-muted-foreground">
                  {dataSiswa?.nama_lengkap}
                </span>
              </div>
            </>
          )}

          <div className="flex justify-between">
            <span className="font-semibold">Biaya Pendaftaran</span>
            <span className="font-semibold text-muted-foreground">
              {formatRupiah(Number(dataInformasiTagihan?.biaya_daftar ?? 0))}
            </span>
          </div>
          {/* <div className="flex justify-between">
            <span className="font-semibold">Biaya Portofolio</span>
            <span className="font-semibold text-muted-foreground">
              {formatRupiah(
                Number(dataInformasiTagihan?.biaya_portofolio ?? 0)
              )}
            </span>
          </div> */}
          <div className="my-3 border-b-2 border-dashed" />
          <div className="flex justify-between">
            <span className="font-semibold">Total Biaya</span>
            <span className="font-semibold">
              {formatRupiah(Number(dataInformasiTagihan?.total_tagihan ?? 0))}
            </span>
          </div>
          <h1 className="my-3 font-bold">PILIH METODE PEMBAYARAN</h1>
          {isPendingListPaymentMethodPlaza ? (
            <div className="flex flex-col gap-y-3">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : (
            <div className="flex flex-col gap-y-3">
              {listPaymentMethodPlaza?.map((paymentMethod: PaymentMethod) => {
                return (
                  <div key={paymentMethod.id}>
                    <div
                      className={`p-3 border rounded-xl hover:bg-secondary cursor-pointer flex-1 overflow-auto ${
                        selectedPaymentMethod?.id === paymentMethod.id
                          ? "border-primary"
                          : ""
                      }`}
                      onClick={() => setSelectedPaymentMethod(paymentMethod)}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={paymentMethod.logo}
                          alt={paymentMethod.name}
                          height={40}
                          width={40}
                        />
                        <span className="font-semibold">
                          {paymentMethod?.name}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <InputError
            text={errorGenerateTagihan?.response?.data?.errors?.payment_gateway_id?.join(
              ","
            )}
          />

          <div
            className={`mt-10 flex gap-3 ${
              isPendingListPaymentMethodPlaza
                ? "pointer-events-none opacity-30"
                : ""
            }`}
          >
            <Button
              variant={"outline"}
              onClick={() => setShowGenerateTagihan(false)}
              disabled={isPendingGenerateTagihan}
            >
              Batal
            </Button>
            <Button disabled={isPendingGenerateTagihan}>
              {isPendingGenerateTagihan ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Buat Tagihan"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showTagihan}
        onClose={() => setShowTagihan(false)}
        width="xl"
        className="z-[999]"
      >
        <FormHeader
          title={isSudahBayar ? "Data Pembayaran" : "Bayar Tagihan Pendaftaran"}
          icon={<FaBitcoin size={20} />}
          description={
            isSudahBayar
              ? "Pembayaran sudah dilakukan."
              : "Lakukan pembayaran tagihan pendaftaran sebelum batas akhir pembyaran"
          }
        />
        <div className="flex flex-col gap-y-5">
          {listDataInvoice?.map((tagihan: DataTagihanDaftarPlaza) => {
            const paymentMethod = listPaymentMethodPlaza?.find(
              (item: PaymentMethod) => {
                return item.id == tagihan.payment_gateway_id
              }
            )

            const isActive =
              selectedTagihan?.id_data_tagihan_daftar_plaza ==
              tagihan.id_data_tagihan_daftar_plaza

            return (
              <section
                key={tagihan.id_data_tagihan_daftar_plaza}
                className={`flex-1 overflow-auto border rounded-xl ${
                  isActive
                    ? "border-primary"
                    : "hover:bg-secondary cursor-pointer"
                }`}
              >
                <section>
                  <div
                    className="p-5 flex items-center justify-between"
                    onClick={() =>
                      setSelectedTagihan((prev) =>
                        prev &&
                        prev?.id_data_tagihan_daftar_plaza ==
                          tagihan?.id_data_tagihan_daftar_plaza
                          ? null
                          : tagihan
                      )
                    }
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={paymentMethod?.logo || ""}
                        alt={paymentMethod?.name || ""}
                        height={50}
                        width={50}
                      />
                      <h1 className="font-bold">{paymentMethod?.name}</h1>
                    </div>
                    <ChevronRight
                      className={`${isActive ? "rotate-90" : ""} duration-300`}
                    />
                  </div>
                  {isActive ? (
                    <div className="p-5 mt-5">
                      {!isSudahBayar && (
                        <BackgroundGradient>
                          <div className="p-3 border rounded-xl bg-white">
                            <h1 className="font-bold">Perhatian!</h1>

                            {tagihan?.payment_gateway_id == 1 && (
                              <div>
                                <span className="italic font-medium text-balance">
                                  Untuk pembayaran briva diharuskan membayar di
                                  media pembayaran BRI. atm, mobile banking,
                                  internet banking, atau hanya di teller BRI.{" "}
                                </span>
                                Prosedur Pembayaran dengan Layanan BRI Virtual
                                Account (BRIVA) dapat di lihat
                                <Link
                                  to={web.linkProsedurPembayaranBRI}
                                  target="_blank"
                                >
                                  <Badge className="ml-2 hover:bg-black/90">
                                    disini
                                  </Badge>
                                </Link>
                              </div>
                            )}

                            {tagihan?.payment_gateway_id == 3 && (
                              <ul className="italic font-medium text-balance">
                                <li className="flex gap-3">
                                  <span>1. </span>
                                  <span className="text-justify">
                                    Pembayaran UKT dan Tagihan Resmi lainnya
                                    dapat dilakukan melalui seluruh channel Bank
                                    BPD Bali seperti BPD Bali Mobile, Internet
                                    Banking Business Bank BPD Bali, Teller Bank
                                    BPD Bali dan Balipay Bank BPD Bali dengan
                                    input "Kode Pembayaran".
                                  </span>
                                </li>
                                <li className="flex gap-3">
                                  <span>2. </span>
                                  <span className="text-justify">
                                    Pembayaran UKT dan Tagihan Resmi lainnya
                                    menggunakan kartu ATM Bank BPD Bali di Mesin
                                    ATM Bank BPD Bali dilakukan dengan input
                                    "0935 + Kode Pembayaran".
                                  </span>
                                </li>
                                <li className="flex gap-3">
                                  <span>3. </span>
                                  <span className="text-justify">
                                    Pembayaran UKT dan Tagihan Resmi lainnya
                                    yang dilakukan melalui Bank lain (transfer
                                    online) dapat menggunakan Virtual Account
                                    Bank BPD Bali "5382 + Kode Pembayaran".
                                  </span>
                                </li>
                                <p className="text-primary">
                                  Prosedur Pembayaran dengan Layanan BPD Virtual
                                  Account
                                  <Link
                                    to={web.linkProsedurPembayaranBPD}
                                    target="_blank"
                                  >
                                    <Badge className="ml-2 hover:bg-black/90">
                                      disini
                                    </Badge>
                                  </Link>
                                </p>
                              </ul>
                            )}
                          </div>
                        </BackgroundGradient>
                      )}
                      <table>
                        <tbody>
                          <tr className="border-b">
                            <th className="py-3 px-3 text-start whitespace-nowrap">
                              Nama Tagihan
                            </th>
                            <td className="py-3 px-3">:</td>
                            <td className="py-3 px-3 underline">
                              {tagihan?.nama_tagihan}
                            </td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 px-3 text-start whitespace-nowrap">
                              Nominal
                            </th>
                            <td className="py-3 px-3">:</td>
                            <td className="py-3 px-3 font-bold text-xl text-primary">
                              {tagihan.amount &&
                                formatRupiah(Number(tagihan?.amount))}
                            </td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 px-3 text-start whitespace-nowrap">
                              Kode Pembayaran
                            </th>
                            <td className="py-3 px-3">:</td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-3">
                                <Badge>{tagihan?.nomor_tagihan}</Badge>

                                <button
                                  className="w-8 h-8 rounded-full hover:bg-secondary flex justify-center items-center"
                                  onClick={() => {
                                    navigator.clipboard
                                      .writeText(`${tagihan?.nomor_tagihan}`)
                                      .then(() => {
                                        toast.success(
                                          "Kode pembayaran telah disalin."
                                        )
                                        setIsCopied(true)
                                        setTimeout(() => {
                                          setIsCopied(false)
                                        }, 3000)
                                      })
                                  }}
                                >
                                  {isCopied ? (
                                    <FaCheck className="text-success" />
                                  ) : (
                                    <MdOutlineContentCopy />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 px-3 text-start whitespace-nowrap">
                              Berlaku Hingga
                            </th>
                            <td className="py-3 px-3">:</td>
                            <td className="py-3 px-3">
                              <Badge className="bg-black text-white hover:bg-black">
                                {tagihan?.expired_date &&
                                  formatDateIndonesia(tagihan?.expired_date)}
                              </Badge>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 px-3 text-start whitespace-nowrap">
                              Keterangan
                            </th>
                            <td className="py-3 px-3">:</td>
                            <td className="py-3 px-3">{tagihan?.keterangan}</td>
                          </tr>
                          <tr className="border-b">
                            <th className="py-3 px-3 text-start whitespace-nowrap">
                              Status Pembayaran
                            </th>
                            <td className="py-3 px-3">:</td>
                            <td className="py-3 px-3">
                              {tagihan?.status_bayar == 2 ? (
                                <Badge variant={"success"}>Sudah dibayar</Badge>
                              ) : tagihan?.status_bayar == 4 ? (
                                <Badge
                                  variant={"success"}
                                  className="hover:bg-secondary"
                                >
                                  Sudah dibayar
                                </Badge>
                              ) : tagihan?.status_bayar == 5 ? (
                                <Badge
                                  variant={"success"}
                                  className="hover:bg-secondary"
                                >
                                  Beasiswa
                                </Badge>
                              ) : tagihan?.status_bayar == 3 ? (
                                <Badge
                                  variant={"destructive"}
                                  className="hover:bg-secondary"
                                >
                                  Expired
                                </Badge>
                              ) : (
                                <Badge variant={"destructive"}>
                                  Belum dibayar
                                </Badge>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    ""
                  )}
                </section>
              </section>
            )
          })}
        </div>
      </Modal>
    </div>
  )
}
